import { motion } from "framer-motion";
import {
  Phone, PhoneIncoming, Mic, FileText, Tag,
  Search, Sparkles, BookOpen, MessageSquare, Mail, Star,
  Mail as MailIcon, TrendingUp, Activity, Send,
  Megaphone, UserCircle2, Bell, Repeat,
  Globe, Instagram, Search as SearchG, Facebook, MapPin, Share2,
  Inbox, Linkedin, Wand2, Smartphone,
} from "lucide-react";

/* ──────────────────────────────────────────────────────────
   1) Call IQ + KAI Receptionist visual
   ────────────────────────────────────────────────────────── */
export const CallIQVisual = () => (
  <div data-testid="visual-call-iq" className="relative">
    {/* Outer phone-card */}
    <div className="relative rounded-3xl border border-border bg-card p-5 shadow-xl shadow-rose-500/[0.08] sm:p-6">
      {/* Top — incoming call header */}
      <div className="flex items-center justify-between rounded-2xl bg-gradient-to-br from-rose-500/[0.1] to-orange-500/[0.05] px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="relative inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-rose-500 to-orange-500 text-white">
            <PhoneIncoming className="h-4 w-4" />
            <span className="absolute -right-0.5 -top-0.5 flex h-3 w-3">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-rose-500" />
            </span>
          </span>
          <div>
            <p className="font-display text-[13px] font-black tracking-tight">Incoming · Sarah M.</p>
            <p className="text-[10.5px] text-muted-foreground">Patient since 2022 · +1 (613) 555-0148</p>
          </div>
        </div>
        <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">
          Picked at ring 3
        </span>
      </div>

      {/* Waveform */}
      <div className="mt-4 flex h-10 items-center gap-[3px] overflow-hidden rounded-xl bg-secondary/60 px-3">
        {[...Array(48)].map((_, i) => (
          <motion.span
            key={i}
            animate={{ scaleY: [0.3, 1, 0.5, 0.9, 0.4, 0.7, 0.3] }}
            transition={{ duration: 1.8, delay: i * 0.04, repeat: Infinity, ease: "easeInOut" }}
            className="block w-[2.5px] flex-none rounded-full bg-gradient-to-b from-rose-400 to-orange-500"
            style={{ height: "60%" }}
          />
        ))}
      </div>

      {/* Live transcript */}
      <div className="mt-4 space-y-2 rounded-xl border border-border bg-background/60 p-3">
        <p className="flex items-center gap-2 font-display text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
          <Mic className="h-3 w-3 text-rose-500" />
          Live transcript
        </p>
        <p className="text-[12px] leading-relaxed text-foreground/85">
          <span className="font-bold text-foreground">KAI:</span> &ldquo;Hi Sarah, this is KAI from ClinicFirst. I see Dr. Wong has an opening Thursday at 10:30…&rdquo;
        </p>
      </div>

      {/* Footer tags */}
      <div className="mt-4 flex flex-wrap gap-1.5">
        {[
          { Ic: Tag,      label: "Intent · Booking" },
          { Ic: FileText, label: "Recorded · Transcribed" },
          { Ic: Sparkles, label: "EMR availability checked" },
        ].map((t, i) => {
          const Ic = t.Ic;
          return (
            <span key={i} className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/40 px-2 py-1 text-[10px] font-semibold text-foreground/80">
              <Ic className="h-3 w-3 text-rose-500" />
              {t.label}
            </span>
          );
        })}
      </div>
    </div>

    {/* Floating desk-phone callout */}
    <div className="absolute -bottom-6 -left-4 hidden rounded-2xl border border-border bg-background p-3 shadow-lg sm:block">
      <div className="flex items-center gap-2.5">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-amber-500 to-rose-500 text-white">
          <Phone className="h-4 w-4" />
        </span>
        <div>
          <p className="font-display text-[11px] font-black uppercase tracking-[0.16em] text-foreground/85">Your desk phone, plugged in</p>
          <p className="text-[10.5px] text-muted-foreground">Not another mobile app · Full custom routing</p>
        </div>
      </div>
    </div>
  </div>
);

/* ──────────────────────────────────────────────────────────
   2) Ask KAI / Knowledge Engine visual
   ────────────────────────────────────────────────────────── */
export const AskKaiVisual = () => (
  <div data-testid="visual-ask-kai" className="relative">
    <div className="relative rounded-3xl border border-border bg-card p-5 shadow-xl shadow-violet-500/[0.08] sm:p-6">
      {/* Wiki header bar */}
      <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/40 px-3.5 py-2.5">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white">
            <BookOpen className="h-3.5 w-3.5" />
          </span>
          <p className="font-display text-[12.5px] font-black tracking-tight">Clinic OS · Wiki</p>
        </div>
        <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">
          v3.2 · Synced
        </span>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-[0.9fr_1.1fr]">
        {/* LEFT — wiki article list */}
        <div className="space-y-1.5">
          <p className="px-1 font-display text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
            Knowledge base
          </p>
          {[
            { Ic: FileText, label: "Late-cancellation policy", note: "Updated 3d ago", active: true },
            { Ic: FileText, label: "Intake script — new pt.",   note: "Updated 1w ago" },
            { Ic: FileText, label: "Insurance receipt template",note: "Updated 2w ago" },
            { Ic: FileText, label: "Recall scripts · physio",   note: "Updated 1m ago" },
          ].map((d, i) => {
            const Ic = d.Ic;
            return (
              <div
                key={i}
                className={`flex items-center gap-2.5 rounded-lg border px-2.5 py-2 ${
                  d.active ? "border-violet-500/35 bg-violet-500/[0.06]" : "border-border bg-background/40"
                }`}
              >
                <span className={`inline-flex h-6 w-6 flex-none items-center justify-center rounded-md ${
                  d.active ? "bg-violet-500/15 text-violet-600 dark:text-violet-300" : "bg-secondary text-muted-foreground"
                }`}>
                  <Ic className="h-3 w-3" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[11.5px] font-bold text-foreground/90">{d.label}</p>
                  <p className="truncate text-[10px] text-muted-foreground">{d.note}</p>
                </div>
              </div>
            );
          })}
          <p className="px-1 pt-1 text-[10.5px] font-medium text-muted-foreground">
            + 47 more articles · all searchable
          </p>
        </div>

        {/* RIGHT — query + answer */}
        <div className="rounded-2xl border border-border bg-secondary/30 p-3">
          <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2">
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <p className="text-[12px]">
              <span className="font-display font-bold">Ask KAI · </span>
              <span className="text-muted-foreground">&ldquo;What&rsquo;s our late-cancel policy?&rdquo;</span>
            </p>
          </div>

          <div className="mt-3 rounded-xl border border-violet-500/25 bg-gradient-to-br from-violet-500/[0.06] to-transparent p-3">
            <p className="inline-flex items-center gap-1.5 font-display text-[10px] font-bold uppercase tracking-[0.18em] text-violet-700 dark:text-violet-300">
              <Sparkles className="h-3 w-3" />
              Answer from your wiki
            </p>
            <p className="mt-2 text-[12.5px] leading-relaxed text-foreground/90">
              Patients cancelling within 24 hrs are charged 50% of the booking fee. Same-week reschedules are exempt. Waive once per patient per year, owner discretion.
            </p>
            <p className="mt-2 inline-flex items-center gap-1 text-[10px] text-muted-foreground">
              <BookOpen className="h-2.5 w-2.5 text-violet-500" />
              Source · Late-cancellation policy.pdf
            </p>
          </div>

          {/* Where this same wiki shows up */}
          <p className="mt-3 font-display text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
            Powers replies on
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {[
              { Ic: Phone,         label: "Calls" },
              { Ic: MessageSquare, label: "SMS" },
              { Ic: Mail,          label: "Email" },
              { Ic: Star,          label: "Reviews" },
              { Ic: Globe,         label: "Web chat" },
              { Ic: BookOpen,      label: "Team wiki" },
            ].map((c) => {
              const Ic = c.Ic;
              return (
                <span key={c.label} className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-1.5 py-0.5 text-[10px] font-semibold text-foreground/80">
                  <Ic className="h-2.5 w-2.5 text-violet-500" />
                  {c.label}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* ──────────────────────────────────────────────────────────
   3) Clinic Pulse — email digest + dashboard visual
   ────────────────────────────────────────────────────────── */
export const ClinicPulseVisual = () => (
  <div data-testid="visual-clinic-pulse" className="relative">
    <div className="relative rounded-3xl border border-border bg-card p-5 shadow-xl shadow-emerald-500/[0.08] sm:p-6">
      {/* Email row */}
      <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/40 px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 text-white">
            <MailIcon className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="truncate font-display text-[12.5px] font-black tracking-tight">Your clinic, this week.</p>
            <p className="truncate text-[10.5px] text-muted-foreground">From: pulse@clinicfirst.app · 06:00 Monday</p>
          </div>
        </div>
        <span className="hidden rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.18em] text-emerald-700 sm:inline dark:text-emerald-300">
          In your inbox
        </span>
      </div>

      {/* Mini-stats grid */}
      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
        {[
          { kicker: "Revenue", value: "+18%",  Ic: TrendingUp, tone: "text-emerald-600 dark:text-emerald-400" },
          { kicker: "No-show", value: "8%",    Ic: Activity,   tone: "text-amber-600 dark:text-amber-400" },
          { kicker: "Recall",  value: "55%",   Ic: Repeat,     tone: "text-blue-600 dark:text-blue-400" },
        ].map((s) => {
          const Ic = s.Ic;
          return (
            <div key={s.kicker} className="rounded-xl border border-border bg-background/60 p-3">
              <div className="flex items-center justify-between">
                <p className="font-display text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{s.kicker}</p>
                <Ic className={`h-3.5 w-3.5 ${s.tone}`} />
              </div>
              <p className="mt-2 font-display text-[1.5rem] font-black leading-none tracking-[-0.025em]">{s.value}</p>
            </div>
          );
        })}
      </div>

      {/* Sparkline */}
      <div className="mt-4 rounded-xl border border-border bg-background/60 p-4">
        <p className="font-display text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Clinician utilisation · last 14 days</p>
        <svg viewBox="0 0 240 56" className="mt-2 h-14 w-full">
          <defs>
            <linearGradient id="pulseGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.45" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d="M0 38 L20 36 L40 30 L60 32 L80 22 L100 24 L120 16 L140 20 L160 12 L180 16 L200 8 L220 14 L240 6" stroke="#10b981" strokeWidth="2" fill="none" />
          <path d="M0 38 L20 36 L40 30 L60 32 L80 22 L100 24 L120 16 L140 20 L160 12 L180 16 L200 8 L220 14 L240 6 L240 56 L0 56 Z" fill="url(#pulseGrad)" />
        </svg>
      </div>

      <p className="mt-3 inline-flex items-center gap-2 text-[11px] text-muted-foreground">
        <Send className="h-3 w-3 text-emerald-500" />
        No exports. No Excel. No daily reminders to anyone.
      </p>
    </div>
  </div>
);

/* ──────────────────────────────────────────────────────────
   4) Reactivation Engine visual
   ────────────────────────────────────────────────────────── */
export const ReactivationVisual = () => (
  <div data-testid="visual-reactivation" className="relative">
    <div className="relative rounded-3xl border border-border bg-card p-5 shadow-xl shadow-amber-500/[0.08] sm:p-6">
      {/* Campaign header */}
      <div className="flex items-center justify-between rounded-2xl bg-gradient-to-br from-amber-500/[0.1] to-rose-500/[0.05] px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-amber-500 to-rose-500 text-white">
            <Megaphone className="h-4 w-4" />
          </span>
          <div>
            <p className="font-display text-[12.5px] font-black tracking-tight">Recall · Manual therapy · Q1</p>
            <p className="text-[10.5px] text-muted-foreground">Auto-built · 87 patients · runs every Friday</p>
          </div>
        </div>
        <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">
          Live
        </span>
      </div>

      {/* Trigger → action flow */}
      <div className="mt-4 grid grid-cols-3 items-center gap-2 text-center">
        <div className="rounded-xl border border-border bg-background/60 p-2.5">
          <p className="font-display text-[9px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Trigger</p>
          <p className="mt-1.5 text-[11px] font-semibold leading-snug text-foreground/85">No visit in 90 days</p>
        </div>
        <div className="flex items-center justify-center">
          <svg viewBox="0 0 40 12" className="h-3 w-full">
            <path d="M0 6 L36 6 M30 1 L36 6 L30 11" stroke="currentColor" strokeWidth="1.5" fill="none" className="text-muted-foreground/60" />
          </svg>
        </div>
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-2.5">
          <p className="font-display text-[9px] font-bold uppercase tracking-[0.18em] text-amber-700 dark:text-amber-300">Send</p>
          <p className="mt-1.5 text-[11px] font-semibold leading-snug text-foreground/85">SMS · then email · then call</p>
        </div>
      </div>

      {/* Patient row preview */}
      <div className="mt-4 space-y-1.5">
        {[
          { name: "Mark D.",    note: "SMS sent · opened 2 min ago" },
          { name: "Priya R.",   note: "Booked via SMS · Thursday 10am" },
          { name: "Jessica L.", note: "Email sent · awaiting reply" },
        ].map((p) => (
          <div key={p.name} className="flex items-center gap-3 rounded-xl border border-border bg-background/60 px-3 py-2">
            <UserCircle2 className="h-6 w-6 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[11.5px] font-semibold text-foreground/90">{p.name}</p>
              <p className="truncate text-[10.5px] text-muted-foreground">{p.note}</p>
            </div>
            <Bell className="h-3.5 w-3.5 text-amber-500" />
          </div>
        ))}
      </div>

      <p className="mt-4 inline-flex items-center gap-2 text-[11px] text-muted-foreground">
        <CheckCircle2Mini /> Tips · newsletter · reactivation — all built-in. No spreadsheets.
      </p>
    </div>
  </div>
);

const CheckCircle2Mini = () => (
  <svg viewBox="0 0 24 24" fill="none" className="h-3 w-3 text-emerald-500">
    <circle cx="12" cy="12" r="11" stroke="currentColor" strokeWidth="2" />
    <path d="M7 12.5L10.5 16L17 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

/* ──────────────────────────────────────────────────────────
   5) Unified Inbox visual — ONE patient's journey across channels.
   Shows the lifecycle as it lives inside the inbox: ad lead → SMS →
   call → booking → reminder → visit → review. Far more relatable than
   a random thread list.
   ────────────────────────────────────────────────────────── */
const JOURNEY = [
  { day: "Day 0", time: "10:14",  Ic: Facebook,      tone: "from-blue-600 to-indigo-600",  channel: "Meta ad lead",  title: "Form submitted",       snippet: "Aisha K. · back-pain assessment · qualified" },
  { day: "Day 0", time: "10:14",  Ic: Smartphone,    tone: "from-rose-400 to-rose-600",    channel: "SMS",           title: "KAI welcomed her",      snippet: "\u201CHi Aisha! I see you\u2019re curious about back pain. Want a 20-min eval?\u201D" },
  { day: "Day 0", time: "10:21",  Ic: Phone,         tone: "from-amber-500 to-rose-500",   channel: "Call",          title: "KAI booked her",        snippet: "Dr. Wong · Tuesday 10:30 · added to Juvonno" },
  { day: "Day 1", time: "08:00",  Ic: Mail,          tone: "from-violet-500 to-purple-600",channel: "Email",         title: "Intake form sent",      snippet: "Pre-visit form filled in 3 minutes" },
  { day: "Day 4", time: "09:30",  Ic: Smartphone,    tone: "from-rose-400 to-rose-600",    channel: "SMS",           title: "Day-of reminder",       snippet: "Auto-sent · she confirmed \u201Csee you soon\u201D" },
  { day: "Day 7", time: "11:42",  Ic: Star,          tone: "from-amber-500 to-orange-500", channel: "Google review", title: "5\u2605 review",        snippet: "\u201CDr. Wong is amazing. Front desk too\u2026\u201D · KAI replied" },
];

export const UnifiedInboxVisual = () => {
  return (
    <div data-testid="visual-unified-inbox" className="relative">
      <div className="relative grid gap-3 rounded-3xl border border-border bg-card p-5 shadow-xl shadow-blue-500/[0.08] sm:p-6 lg:grid-cols-[1fr_1.1fr]">
        {/* LEFT — patient header + journey timeline */}
        <div>
          <div className="flex items-center gap-3 rounded-2xl border border-border bg-secondary/40 px-3 py-2.5">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 font-display text-[13px] font-black text-white">
              AK
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate font-display text-[13px] font-black tracking-tight">Aisha K. · new patient</p>
              <p className="truncate text-[10.5px] text-muted-foreground">From: Meta ad · qualified Day 0 · booked Day 0</p>
            </div>
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">
              <span className="h-1 w-1 rounded-full bg-emerald-500" />
              Live
            </span>
          </div>

          <p className="mt-3 px-1 font-display text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
            Patient journey · 1 thread
          </p>

          <ol className="mt-2 space-y-2.5">
            {JOURNEY.map((e, i) => {
              const Ic = e.Ic;
              const isActive = i === 2; // highlight the "KAI booked her" moment
              return (
                <li key={i} className="relative flex items-start gap-3">
                  {/* time rail */}
                  <div className="w-12 flex-none text-right">
                    <p className="font-display text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{e.day}</p>
                    <p className="text-[9.5px] text-muted-foreground/85">{e.time}</p>
                  </div>
                  {/* dot + vertical line */}
                  <div className="relative flex h-full flex-none flex-col items-center">
                    <span className={`mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br ${e.tone} text-white shadow ring-2 ring-card`}>
                      <Ic className="h-3 w-3" />
                    </span>
                    {i < JOURNEY.length - 1 && <span className="mt-1 h-full w-px flex-1 bg-border" />}
                  </div>
                  {/* event content */}
                  <div className={`min-w-0 flex-1 rounded-lg border px-3 py-2 ${isActive ? "border-amber-500/40 bg-amber-500/[0.06]" : "border-border bg-background/40"}`}>
                    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{e.channel} · {e.title}</p>
                    <p className="mt-0.5 truncate text-[11.5px] text-foreground/85">{e.snippet}</p>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        {/* RIGHT — open thread + KAI draft (one of the journey steps zoomed in) */}
        <div className="rounded-2xl border border-border bg-secondary/30 p-4">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-amber-500 to-rose-500 text-white">
              <Phone className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p className="truncate font-display text-[12.5px] font-black tracking-tight">Day 0 · 10:21 · Call with Aisha</p>
              <p className="truncate text-[10.5px] text-muted-foreground">KAI · transcribed · booked into Juvonno</p>
            </div>
          </div>

          <div className="mt-3 space-y-2">
            <div className="rounded-xl border border-border bg-card p-2.5">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Aisha</p>
              <p className="mt-0.5 text-[11.5px] leading-relaxed text-foreground/85">
                &ldquo;Hi! I just filled out the form. Can I see someone this week?&rdquo;
              </p>
            </div>
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-2.5">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-amber-700 dark:text-amber-300">KAI</p>
              <p className="mt-0.5 text-[11.5px] leading-relaxed text-foreground/90">
                &ldquo;Of course Aisha. Dr. Wong has Tuesday 10:30 or Thursday 2:15 — both work for a back-pain eval. Which one?&rdquo;
              </p>
            </div>
          </div>

          {/* Snippets row */}
          <p className="mt-4 font-display text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
            Reply with one tap
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {["Confirm appt","Send intake form","Insurance receipt","Late-cancel policy"].map((s) => (
              <span key={s} className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 text-[10px] font-semibold text-foreground/75">
                <Sparkles className="h-2.5 w-2.5 text-blue-500" />
                {s}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Channel badges below the card */}
      <div className="mt-4 flex flex-wrap items-center justify-center gap-1.5">
        {[
          { Ic: Mail,          label: "Email" },
          { Ic: Smartphone,    label: "SMS" },
          { Ic: MessageSquare, label: "WhatsApp" },
          { Ic: Star,          label: "Reviews" },
          { Ic: Facebook,      label: "Meta" },
          { Ic: Instagram,     label: "Instagram" },
          { Ic: SearchG,       label: "Google" },
          { Ic: Linkedin,      label: "LinkedIn" },
          { Ic: MapPin,        label: "GMB" },
          { Ic: Share2,        label: "Referrals" },
        ].map((c) => {
          const Ic = c.Ic;
          return (
            <span
              key={c.label}
              data-testid={`unified-channel-${c.label.toLowerCase()}`}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-card/80 px-2 py-1 text-[10.5px] font-semibold text-foreground/80"
            >
              <Ic className="h-3 w-3 text-blue-500" />
              {c.label}
            </span>
          );
        })}
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────
   ChannelHub — "Every lead lands here, natively connected"
   ────────────────────────────────────────────────────────── */
const CHANNELS = [
  { Ic: Globe,       label: "Website",   color: "from-violet-500 to-fuchsia-500" },
  { Ic: Instagram,   label: "Social",    color: "from-rose-500 to-orange-500" },
  { Ic: SearchG,     label: "Google",    color: "from-blue-500 to-sky-500" },
  { Ic: Facebook,    label: "Meta ads",  color: "from-blue-600 to-blue-700" },
  { Ic: MapPin,      label: "Walk-ins",  color: "from-emerald-500 to-teal-500" },
  { Ic: Share2,      label: "Referrals", color: "from-amber-500 to-rose-500" },
];

export const ChannelHub = () => (
  <section data-testid="channel-hub" className="relative overflow-hidden border-t border-border bg-secondary/30 py-12 sm:py-16">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
      <div className="grid items-end gap-6 lg:grid-cols-[1.1fr_1fr]">
        <div>
          <span className="font-display text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
            Every channel, one place
          </span>
          <h2 className="mt-2 font-display text-[1.75rem] font-black leading-[1.02] tracking-[-0.025em] sm:text-[2.125rem] lg:text-[2.5rem]">
            Every lead. Every channel.{" "}
            <span className="bg-gradient-to-br from-amber-500 via-rose-500 to-fuchsia-500 bg-clip-text text-transparent">One inbox.</span>
          </h2>
        </div>
        <p className="text-[14px] leading-relaxed text-muted-foreground sm:text-[15px]">
          Calls, social DMs, ad enquiries, Google leads, walk-in forms, referrals — every touchpoint lands in the same place, attributed to the channel it came from. No Zapier in the middle.
        </p>
      </div>

      <div className="relative mt-10">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6 lg:gap-4">
          {CHANNELS.map((c, i) => {
            const Ic = c.Ic;
            return (
              <motion.div
                key={c.label}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                data-testid={`channel-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-card px-3 py-5 text-center shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
              >
                <span className={`inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br ${c.color} text-white shadow-sm ring-1 ring-inset ring-white/15`}>
                  <Ic className="h-5 w-5" />
                </span>
                <span className="font-display text-[12.5px] font-black tracking-tight text-foreground/90">
                  {c.label}
                </span>
              </motion.div>
            );
          })}
        </div>

        {/* Central inbox label */}
        <div className="mx-auto mt-8 max-w-md rounded-2xl border-2 border-foreground/15 bg-card p-5 text-center shadow-lg">
          <p className="font-display text-[10.5px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
            They all flow into
          </p>
          <p className="mt-2 font-display text-[1.625rem] font-black leading-none tracking-[-0.025em] sm:text-[1.875rem]">
            <span className="bg-gradient-to-br from-amber-500 via-rose-500 to-fuchsia-500 bg-clip-text text-transparent">
              Your Clinic OS inbox.
            </span>
          </p>
          <p className="mt-3 text-[12.5px] leading-relaxed text-muted-foreground">
            With the source channel attached. Routed to the right team. Replied to by KAI or by your team — your call.
          </p>
        </div>
      </div>
    </div>
  </section>
);
